import type { Step } from './flow';
import {
  isReservedFieldKey,
  REQUIRED_EVENT_REGISTRATION_FIELD_KEYS,
} from './flow';

export {
  isReservedFieldKey,
  PERSON_FIELD_PRESETS,
  REQUIRED_EVENT_REGISTRATION_FIELD_KEYS,
  RESERVED_PERSON_FIELD_KEYS,
  type ReservedPersonFieldKey,
} from './flow';

/** True when flow looks like event registration (binding or reserved keys). */
export function isEventRegistrationFlow(steps: Step[]): boolean {
  const fields = steps.flatMap((s) => s.fields ?? []);
  const hasEventBinding = fields.some(
    (f) =>
      f.binding?.type === 'query_param' &&
      /^event[_-]?id$/i.test((f.binding.name || '').trim())
  );
  const hasEventFieldKey = fields.some(
    (f) => isReservedFieldKey(f.fieldKey) && f.fieldKey === 'event_id'
  );
  const hasAnyPersonKey = fields.some((f) =>
    (REQUIRED_EVENT_REGISTRATION_FIELD_KEYS as readonly string[]).includes(
      f.fieldKey || ''
    )
  );
  return hasEventBinding || hasEventFieldKey || hasAnyPersonKey;
}

/**
 * Validate reserved person fieldKeys before publish.
 * Returns human-readable error messages (empty = ok).
 */
export function validatePersonFieldKeysForPublish(steps: Step[]): string[] {
  const errors: string[] = [];
  const fields = steps.flatMap((s) => s.fields ?? []);

  for (const step of steps) {
    const reservedInStep = (step.fields ?? [])
      .map((f) => f.fieldKey)
      .filter((k): k is string => !!k && isReservedFieldKey(k));
    const seen = new Set<string>();
    for (const key of reservedInStep) {
      if (seen.has(key)) {
        errors.push(
          `Step "${step.title || step.id}": duplicate reserved fieldKey "${key}"`
        );
      }
      seen.add(key);
    }
  }

  if (!isEventRegistrationFlow(steps)) {
    return errors;
  }

  const allKeys = new Set(
    fields.map((f) => f.fieldKey).filter((k): k is string => !!k)
  );

  const hasEventId =
    fields.some(
      (f) =>
        f.binding?.type === 'query_param' &&
        /^event[_-]?id$/i.test((f.binding.name || '').trim())
    ) || allKeys.has('event_id');

  if (!hasEventId) {
    errors.push(
      'Event registration flows need an Event ID field (fieldKey event_id) or a query_param binding named eventId'
    );
  }

  for (const required of REQUIRED_EVENT_REGISTRATION_FIELD_KEYS) {
    if (!allKeys.has(required)) {
      errors.push(
        `Event registration flows require a field with fieldKey "${required}"`
      );
    }
  }

  return errors;
}

export function warnIfSlugCollidesWithReserved(
  proposedKey: string,
  currentKey: string | undefined
): string | null {
  if (
    isReservedFieldKey(proposedKey) &&
    currentKey !== proposedKey &&
    !isReservedFieldKey(currentKey)
  ) {
    return `Slug "${proposedKey}" is a reserved person field key. Use the Person field preset or pick a different label.`;
  }
  return null;
}
